import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useFocusEffect, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { prayersReadingsService, PrayersReadingsRec } from '../../../../services/prayersReadingsInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('PrayersReadingsData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value?: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  if (!value || value.trim() === '') return null;

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{value}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const PrayersReadingsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const { targetUserId, viewOnly } = route.params || {};
  const [prayersReadingsData, setPrayersReadingsData] = useState<PrayersReadingsRec | null>(null);
  const [loading, setLoading] = useState(true);

  const loadPrayersReadings = async () => {
    try {
      setLoading(true);
      const allPrayersReadings = await prayersReadingsService.getAllPrayersReadings(targetUserId);

      if (allPrayersReadings && allPrayersReadings.length > 0) {
        // Get the most recent entry
        const mostRecent = allPrayersReadings[0];
        setPrayersReadingsData(mostRecent);
      } else {
        setPrayersReadingsData(null);
      }
    } catch (error) {
      logger.error('Error loading prayers and readings:', error);
      Alert.alert('Error', 'Failed to load prayers and readings data');
    } finally {
      setLoading(false);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      loadPrayersReadings();
    }, [])
  );

  const handleEdit = () => {
    if (prayersReadingsData) {
      navigation.navigate('PrayersReadingsInput', {
        prayersReadingsData,
        isEditing: true,
      });
    } else {
      navigation.navigate('PrayersReadingsInput');
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
          <Text style={styles.loadingText}>Loading prayers and readings...</Text>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Prayers & Readings Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {!prayersReadingsData ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyText}>No prayers and readings data saved yet</Text>
              <Text style={styles.emptySubtext}>
                Tap "Edit" below to add your prayers and readings preferences
              </Text>
            </View>
          ) : (
            <>
              <InfoItem
                label="Religious Preference"
                value={prayersReadingsData.religiousPreference}
              />
              <InfoItem
                label="Specific Prayers"
                value={prayersReadingsData.specificPrayers}
              />
              <InfoItem
                label="Scripture Readings"
                value={prayersReadingsData.scriptureReadings}
              />
              <InfoItem
                label="Poems & Secular Readings"
                value={prayersReadingsData.poemsReadings}
              />
              <InfoItem
                label="Preferred Readers"
                value={prayersReadingsData.preferredReaders}
              />
              <InfoItem
                label="Language Preference"
                value={prayersReadingsData.languagePreference}
              />
              <InfoItem
                label="Cultural Traditions"
                value={prayersReadingsData.culturalTraditions}
              />
              <InfoItem
                label="Special Blessings"
                value={prayersReadingsData.specialBlessings}
              />
              <InfoItem
                label="Content to Avoid"
                value={prayersReadingsData.avoidContent}
              />
              <InfoItem
                label="Additional Notes"
                value={prayersReadingsData.notes}
              />
            </>
          )}

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('FinalWishesChecklist')}
            >
              <Text style={styles.buttonText}>Back</Text>
            </TouchableOpacity>

            {!viewOnly && (
              <TouchableOpacity
                style={[styles.button, styles.editButton]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>
                  {prayersReadingsData ? 'Edit' : 'Add'}
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  emptyState: {
    padding: ms(40),
    alignItems: 'center',
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(15),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default PrayersReadingsDataScreen;
